/*
 * offer-display.js — generic offer display custom element.
 * Standalone asset (theme.js is a compiled, unrebuildable bundle).
 *
 * FRONT END ONLY. Applies no discount, validates no discount state.
 * Two modes:
 *   gift_picker    — single/multi select, adds qty 1 with a line item property,
 *                    swaps on re-select, qty-lock (in cart template), never-alone guard,
 *                    disabled until the cart holds a non-charm line (PDP timing option b).
 *   promo_carousel — plain add-to-cart, no property, no guards.
 *
 * Re-renders piggyback Prestige's cart:refresh / cart:change (CartDrawer self-refreshes
 * via the Section Rendering API). We only dispatch those events; we never touch the drawer.
 */
(function () {
  if (window.customElements.get('offer-display')) return;

  class OfferDisplay extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'gift_picker';
      this.placement = this.dataset.placement || 'product';
      this.live = this.dataset.live === '1';
      this.propKey = this.dataset.propertyKey || '_offer';
      this.propVal = this.dataset.propertyValue || 'free-charm';
      this.selectionMode = this.dataset.selectionMode || 'single';
      this.max = parseInt(this.dataset.max, 10) || 1;
      this.eligibility = this.dataset.eligibility || 'always';
      this.minSubtotal = (parseFloat(this.dataset.minSubtotal) || 0) * 100; // cents
      this.minItems = parseInt(this.dataset.minItems, 10) || 0;
      this.status = this.querySelector('[data-offer-status]');
      this.busy = false;

      // Editor: static preview only, no cart calls.
      if (!this.live) return;

      if (this.mode === 'gift_picker') {
        this._initGift();
      } else {
        this._initPromo();
      }

      // Re-check eligibility whenever the cart changes (theme pub/sub).
      this._onCartChange = () => this._refreshEligibility();
      document.addEventListener('cart:change', this._onCartChange);
      document.addEventListener('cart:refresh', this._onCartChange);
      this._refreshEligibility();
    }

    disconnectedCallback() {
      if (this._onCartChange) {
        document.removeEventListener('cart:change', this._onCartChange);
        document.removeEventListener('cart:refresh', this._onCartChange);
      }
    }

    /* ---------------- gift picker ---------------- */
    _initGift() {
      this.radios = Array.from(this.querySelectorAll('.offer-display__radio'));
      this.radios.forEach((r) => r.addEventListener('change', () => this._onSelect(r)));
    }

    async _onSelect(radio) {
      if (this.busy || radio.disabled) return;
      if (!this._eligible) {
        this._say(this.dataset.emptyMessage || '', false);
        radio.checked = false;
        return;
      }
      // require_variant_selection: cards render the first available variant only.
      // If the product has multiple variants and a choice is required, we cannot pick
      // it inline (draft limitation — see handoff). We proceed with the first variant.
      const variantId = parseInt(radio.value, 10);
      this._lock(true);
      try {
        // Swap: in single mode, remove any existing charm line first (sequential, UI locked).
        if (this.selectionMode === 'single') {
          await this._removeCharmLines();
        } else if (this._charmCount() >= this.max) {
          this._say(this.dataset.maxMessage || '', false);
          return;
        }
        const json = await this._add(variantId, { [this.propKey]: this.propVal });

        if (this._isCartPage()) return void window.location.reload();

        // baseEvent is deliberately NOT 'variant:add': CartDrawer re-renders on any
        // cart:change but only calls show() for 'variant:add' (theme.js:4213). A charm
        // pick updates the cart + drawer silently instead of yanking the drawer open.
        await this._publishAdd(json, 'offer:add');
        await this._guardNeverAlone();
        this._say(this.dataset.successMessage || '', false);
      } catch (e) {
        this._say((e && e.message) || 'Something went wrong.', true);
        radio.checked = false;
        document.dispatchEvent(new CustomEvent('cart:refresh')); // resync, mirrors theme.js:2724
      } finally {
        this._lock(false);
      }
    }

    /* ---------------- promo carousel ---------------- */
    _initPromo() {
      this.querySelectorAll('[data-offer-add]').forEach((btn) => {
        btn.addEventListener('click', () => this._promoAdd(btn));
      });
    }

    async _promoAdd(btn) {
      if (this.busy || btn.disabled) return;
      const variantId = parseInt(btn.dataset.variant, 10);
      this._lock(true);
      btn.setAttribute('aria-busy', 'true');
      try {
        const json = await this._add(variantId, null);
        if (this._isCartPage()) return void window.location.reload();
        // Promo = a normal add-to-cart, so mirror ProductForm exactly: baseEvent
        // 'variant:add' lets CartDrawer open, which is what a normal add does.
        await this._publishAdd(json, 'variant:add');
        btn.disabled = true;
        if (btn.dataset.addedLabel) btn.textContent = btn.dataset.addedLabel;
      } catch (e) {
        this._say((e && e.message) || 'Could not add to cart.', true);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        btn.removeAttribute('aria-busy');
        this._lock(false);
      }
    }

    /* ---------------- cart plumbing (mirrors Prestige ProductForm, theme.js:2676) ---------------- */

    _root() {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    }

    _isCartPage() {
      // Prestige itself full-reloads the cart page rather than partial-rendering
      // (theme.js:1466 and 2696). Mirror that.
      return window.themeVariables?.settings?.pageType === 'cart';
    }

    // Sections self-register here (CartDrawer does, theme.js:4208). Without this the
    // add response carries no rendered section HTML and nothing can re-render.
    _bundledSections() {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
      );
      return sections;
    }

    async _add(id, properties) {
      const sections = this._bundledSections();
      const formData = new FormData();
      formData.set('id', id);
      formData.set('quantity', '1');
      if (properties) {
        Object.keys(properties).forEach((k) => formData.set(`properties[${k}]`, properties[k]));
      }
      formData.set('sections', sections.join(','));
      formData.set('sections_url', window.location.pathname);
      const res = await fetch(`${this._root()}cart/add.js`, {
        body: formData,
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }, // needed for inventory checks
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || 'Add failed');
      return json;
    }

    // change.js returns the cart object with `sections` already attached (theme.js:1443).
    async _changeLine(lineKey, quantity) {
      const sections = this._bundledSections();
      const res = await fetch(`${this._root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity, sections: sections.join(',') }),
      });
      if (!res.ok) throw new Error('Could not update cart');
      return res.json();
    }

    // ProductForm builds cart content then attaches the rendered sections (theme.js:2699).
    async _publishAdd(responseJson, baseEvent) {
      const cartContent = await this._getCart();
      cartContent['sections'] = responseJson['sections'];
      this._publish(cartContent, baseEvent);
      return cartContent;
    }

    // detail.cart is REQUIRED: CartDrawer reads detail.cart.sections[id] (4212) and
    // CartCount reads detail.cart.item_count (1290). Omitting it throws in their handlers.
    _publish(cartContent, baseEvent) {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent, cart: cartContent } })
      );
    }

    async _getCart() {
      const res = await fetch(`${this._root()}cart.js`, { headers: { Accept: 'application/json' } });
      return res.json();
    }

    _isCharm(item) {
      return item.properties && String(item.properties[this.propKey]) === String(this.propVal);
    }

    _charmCount() {
      return this._lastCharmKeys ? this._lastCharmKeys.length : 0;
    }

    // Returns the last cart payload from change.js (already carries `sections`), or null.
    async _removeCharmLines() {
      const cart = await this._getCart();
      const charms = cart.items.filter((i) => this._isCharm(i));
      let last = null;
      for (const line of charms) {
        // change.js keyed on line item key (not variant id — a variant can appear twice).
        last = await this._changeLine(line.key, 0);
      }
      return last;
    }

    // Never let a charm sit alone: if every line is a charm, remove them all.
    async _guardNeverAlone() {
      const cart = await this._getCart();
      const nonCharm = cart.items.filter((i) => !this._isCharm(i));
      this._lastCharmKeys = cart.items.filter((i) => this._isCharm(i)).map((c) => c.key);
      if (cart.items.length > 0 && nonCharm.length === 0) {
        const after = await this._removeCharmLines();
        this._lastCharmKeys = [];
        if (after) this._publish(after, 'line-item:change');
        this._say(this.dataset.emptyMessage || '', false);
      }
    }

    async _refreshEligibility() {
      if (this.mode !== 'gift_picker' || !this.live) return;
      let cart;
      try { cart = await this._getCart(); } catch (e) { return; }
      const nonCharm = cart.items.filter((i) => !this._isCharm(i));
      this._lastCharmKeys = cart.items.filter((i) => this._isCharm(i)).map((c) => c.key);

      let eligible = true;
      // Never-alone precondition (PDP timing option b): need at least one non-charm line.
      if (nonCharm.length === 0) eligible = false;

      switch (this.eligibility) {
        case 'min_cart_subtotal':
          if (cart.items_subtotal_price < this.minSubtotal) eligible = false;
          break;
        case 'min_cart_items': {
          const count = nonCharm.reduce((n, i) => n + i.quantity, 0);
          if (count < this.minItems) eligible = false;
          break;
        }
        default:
          break; // 'always' — still subject to the never-alone precondition above.
      }

      this._eligible = eligible;
      this.classList.toggle('is-locked', !eligible);
      if (this.radios) this.radios.forEach((r) => { r.disabled = !eligible || r.dataset.soldOut === '1'; });

      // Reflect current selection from the live cart.
      if (this.radios) {
        const charmVariantIds = cart.items.filter((i) => this._isCharm(i)).map((i) => String(i.variant_id));
        this.radios.forEach((r) => { r.checked = charmVariantIds.includes(String(r.value)); });
      }

      // Only ever clear our own ineligibility notice — never a success message set by
      // _onSelect, which races with the cart:change this same flow dispatched.
      if (!eligible && this.dataset.emptyMessage) this._say(this.dataset.emptyMessage, false);
      else if (eligible && this.status && this.status.textContent === this.dataset.emptyMessage) this._hide();
    }

    /* ---------------- ui helpers ---------------- */
    // is-busy blocks pointer events in CSS, so a customer with no visual feedback
    // cannot fire a second add mid-request. `busy` also short-circuits the handlers.
    _lock(on) {
      this.busy = on;
      this.classList.toggle('is-busy', on);
      if (on) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
    }

    _say(msg, isError) {
      if (!this.status || !msg) return;
      this.status.textContent = msg;
      this.status.hidden = false;
      this.status.style.color = isError ? 'var(--color-error, #c00)' : '';
    }

    _hide() {
      if (this.status) this.status.hidden = true;
    }
  }

  window.customElements.define('offer-display', OfferDisplay);
})();
